import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ImageUpload from '@/components/ImageUpload';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import {
  User,
  Phone,
  MapPin,
  Edit,
  Save,
  X,
  Heart,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const EditProfile = () => {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    location: '',
    profile_image: '',
    date_of_birth: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    gender: '',
    blood_type: '',
    allergies: '',
    emergency_contact: '',
    emergency_phone: '',
  });

  // Date dropdown options
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '01', label: 'Janvier' },
    { value: '02', label: 'Février' },
    { value: '03', label: 'Mars' },
    { value: '04', label: 'Avril' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juin' },
    { value: '07', label: 'Juillet' },
    { value: '08', label: 'Août' },
    { value: '09', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' },
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (user) {
      // Parse existing date_of_birth into components
      let birthDay = '', birthMonth = '', birthYear = '';
      if (user.date_of_birth) {
        const parts = user.date_of_birth.split('-');
        if (parts.length === 3) {
          birthYear = parts[0];
          birthMonth = parts[1];
          birthDay = parseInt(parts[2], 10).toString();
        }
      }
      
      setFormData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        location: user.location || '',
        profile_image: user.profile_image || '',
        date_of_birth: user.date_of_birth || '',
        birth_day: birthDay,
        birth_month: birthMonth,
        birth_year: birthYear,
        gender: user.gender || '',
        blood_type: user.blood_type || '',
        allergies: user.allergies || '',
        emergency_contact: user.emergency_contact || '',
        emergency_phone: user.emergency_phone || '',
      });
    }
  }, [user]);

  // Update date_of_birth when components change
  useEffect(() => {
    if (formData.birth_day && formData.birth_month && formData.birth_year) {
      const day = formData.birth_day.toString().padStart(2, '0');
      const dateStr = `${formData.birth_year}-${formData.birth_month}-${day}`;
      if (dateStr !== formData.date_of_birth) {
        setFormData(prev => ({ ...prev, date_of_birth: dateStr }));
      }
    }
  }, [formData.birth_day, formData.birth_month, formData.birth_year]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await usersAPI.updateProfile(formData);
      setUser(response.data.user);
      localStorage.setItem('dockhta_user', JSON.stringify(response.data.user));
      toast.success('Profil mis à jour avec succès !');
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'patient': return 'Patient';
      case 'professional': return 'Professionnel';
      case 'agent': return 'Agent terrain';
      case 'admin': return 'Administrateur';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'patient': return 'bg-blue-100 text-blue-700';
      case 'professional': return 'bg-red-100 text-red-600';
      case 'agent': return 'bg-amber-100 text-amber-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="edit-profile-page">
      <h1 
        className="text-2xl font-bold text-[#1A237E] mb-6"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        Mon profil
      </h1>

      {/* Profile Header Card */}
      <Card className="border-0 shadow-lg mb-6 overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-[#E53935] via-[#2196F3] to-[#1A237E]" />
        <CardContent className="relative -mt-16 pb-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="relative">
              {editing ? (
                <ImageUpload
                  currentImage={formData.profile_image}
                  onImageUploaded={(url) => setFormData({ ...formData, profile_image: url })}
                  type="profile"
                  size="lg"
                />
              ) : (
                <Avatar className="w-28 h-28 border-4 border-white shadow-lg">
                  <AvatarImage src={formData.profile_image} />
                  <AvatarFallback className="bg-red-100 text-red-600 text-3xl">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900">{user?.full_name}</h2>
              <p className="text-slate-500">{user?.email}</p>
              <Badge className={`mt-2 ${getRoleColor(user?.role)}`}>
                {getRoleLabel(user?.role)}
              </Badge>
            </div>
            {!editing ? (
              <Button 
                onClick={() => setEditing(true)} 
                className="bg-[#1A237E] hover:bg-[#3949AB] rounded-full gap-2"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-4 h-4" />
                Modifier
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setEditing(false)}
                  className="rounded-full"
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
                  data-testid="save-profile-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-[#E53935]" />
                Informations personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nom complet</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  disabled={!editing}
                  className="h-11"
                  data-testid="fullname-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={!editing}
                    placeholder="+237 6XX XXX XXX"
                    className="pl-10 h-11"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Localisation</Label>
                {editing ? (
                  <AddressAutocomplete
                    value={formData.location}
                    onChange={(value) => setFormData({ ...formData, location: value })}
                    onSelect={(location) => {
                      const cityName = location.address?.city || location.address?.town || 
                                      location.address?.village || location.display_name.split(',')[0];
                      setFormData(prev => ({ ...prev, location: cityName }));
                    }}
                    placeholder="Rechercher une ville..."
                    data-testid="location-input"
                  />
                ) : (
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.location}
                      disabled
                      placeholder="Non renseigné"
                      className="pl-10 h-11"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {/* Date de naissance */}
                <div className="space-y-2">
                  <Label>Date de naissance</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      value={formData.birth_day?.toString() || ''}
                      onValueChange={(value) => setFormData({ ...formData, birth_day: value })}
                      disabled={!editing}
                    >
                      <SelectTrigger className="h-12 min-h-[48px]">
                        <SelectValue placeholder="Jour" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {days.map((day) => (
                          <SelectItem key={day} value={day.toString()} className="h-11">{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={formData.birth_month || ''}
                      onValueChange={(value) => setFormData({ ...formData, birth_month: value })}
                      disabled={!editing}
                    >
                      <SelectTrigger className="h-12 min-h-[48px]">
                        <SelectValue placeholder="Mois" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {months.map((m) => (
                          <SelectItem key={m.value} value={m.value} className="h-11">{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={formData.birth_year?.toString() || ''}
                      onValueChange={(value) => setFormData({ ...formData, birth_year: value })}
                      disabled={!editing}
                    >
                      <SelectTrigger className="h-12 min-h-[48px]">
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()} className="h-11">{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Sexe */}
                <div className="space-y-2">
                  <Label>Sexe</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => editing && setFormData({ ...formData, gender: 'male' })}
                      disabled={!editing}
                      className={`h-11 rounded-xl border-2 font-medium text-sm transition-all ${
                        formData.gender === 'male'
                          ? 'border-[#1976D2] bg-[#1976D2]/10 text-[#1976D2]'
                          : 'border-slate-200 text-slate-600'
                      } ${!editing ? 'opacity-60 cursor-not-allowed' : 'hover:border-slate-300'}`}
                    >
                      Masculin
                    </button>
                    <button
                      type="button"
                      onClick={() => editing && setFormData({ ...formData, gender: 'female' })}
                      disabled={!editing}
                      className={`h-11 rounded-xl border-2 font-medium text-sm transition-all ${
                        formData.gender === 'female'
                          ? 'border-[#E91E63] bg-[#E91E63]/10 text-[#E91E63]'
                          : 'border-slate-200 text-slate-600'
                      } ${!editing ? 'opacity-60 cursor-not-allowed' : 'hover:border-slate-300'}`}
                    >
                      Féminin
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Medical Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#E53935]" />
                Informations médicales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Groupe sanguin</Label>
                <Select
                  value={formData.blood_type}
                  onValueChange={(value) => setFormData({ ...formData, blood_type: value })}
                  disabled={!editing}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Allergies connues</Label>
                <Textarea
                  value={formData.allergies}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  disabled={!editing}
                  placeholder="Ex: Pénicilline, arachides..."
                  className="min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card className="border-0 shadow-sm md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Contact d&apos;urgence
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du contact</Label>
                  <Input
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                    disabled={!editing}
                    placeholder="Nom de la personne à contacter"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone d&apos;urgence</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.emergency_phone}
                      onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                      disabled={!editing}
                      placeholder="+237 6XX XXX XXX"
                      className="pl-10 h-11"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
